import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useToast, Toast, ToastType } from '../../../hooks/useToast';
import { logger } from '../../../utils/logger';

interface ToastContainerProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center';
  maxToasts?: number;
}

const getToastIcon = (type: ToastType) => {
  switch (type) {
    case 'success':
      return <CheckCircle className="w-5 h-5 text-[color-mix(in_srgb,var(--accent-success) 70%, var(--text) 30%)]" />;
    case 'error':
      return <XCircle className="w-5 h-5 text-[color-mix(in_srgb,var(--accent-secondary) 72%, var(--text) 28%)]" />;
    case 'warning':
      return <AlertTriangle className="w-5 h-5 text-[color-mix(in_srgb,var(--accent-warning) 68%, var(--text) 32%)]" />;
    case 'info':
      return <Info className="w-5 h-5 text-[color-mix(in_srgb,var(--accent-primary) 70%, var(--text) 30%)]" />;
    default:
      return <Info className="w-5 h-5 text-[color-mix(in_srgb,var(--accent-primary) 70%, var(--text) 30%)]" />;
  }
};

const getToastStyles = (type: ToastType) => {
  switch (type) {
    case 'success':
      return 'bg-[color-mix(in_srgb,var(--accent-success) 12%, var(--bg) 88%)] border-[color-mix(in_srgb,var(--accent-success) 35%, transparent)] text-[color-mix(in_srgb,var(--accent-success) 70%, var(--text) 30%)]';
    case 'error':
      return 'bg-[color-mix(in_srgb,var(--accent-secondary) 12%, var(--bg) 88%)] border-[color-mix(in_srgb,var(--accent-secondary) 35%, transparent)] text-[color-mix(in_srgb,var(--accent-secondary) 68%, var(--text) 32%)]';
    case 'warning':
      return 'bg-[color-mix(in_srgb,var(--accent-warning) 16%, var(--bg) 84%)] border-[color-mix(in_srgb,var(--accent-warning) 35%, transparent)] text-[color-mix(in_srgb,var(--accent-warning) 62%, var(--text) 38%)]';
    case 'info':
      return 'bg-[color-mix(in_srgb,var(--accent-primary) 12%, var(--bg) 88%)] border-[color-mix(in_srgb,var(--accent-primary) 40%, transparent)] text-[color-mix(in_srgb,var(--accent-primary) 80%, var(--text) 20%)]';
    default:
      return 'bg-[var(--bg-elev)] border-[var(--border-subtle)] text-[var(--text)]';
  }
};

const ToastItem: React.FC<{ toast: Toast; onClose: (id: string) => void }> = ({ toast, onClose }) => {
  const handleClose = () => {
    logger.debug(`Toast closed: ${toast.type} - ${toast.title}`, undefined, 'Toast');
    onClose(toast.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -50, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={`relative max-w-sm w-full bg-[var(--bg-elev)] border border-[var(--border-subtle)] rounded-lg shadow-[0_12px_24px_color-mix(in_srgb,var(--shadow) 24%,transparent)] p-4 ${getToastStyles(toast.type)}`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getToastIcon(toast.type)}
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-semibold">{toast.title}</h3>
          {toast.message && (
            <p className="mt-1 text-sm opacity-90">{toast.message}</p>
          )}
          {toast.action && (
            <button
              onClick={toast.action.onClick}
              className="mt-2 text-sm font-medium underline hover:no-underline"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={handleClose}
              className="inline-flex text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const ToastContainer: React.FC<ToastContainerProps> = ({ 
  position = 'top-right', 
  maxToasts = 5 
}) => {
  const { toasts, hideToast } = useToast();

  const getPositionClasses = () => {
    switch (position) {
      case 'top-right':
        return 'top-4 right-4';
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-center':
        return 'top-4 left-1/2 transform -translate-x-1/2';
      default:
        return 'top-4 right-4';
    }
  };

  // Limit number of toasts displayed
  const displayToasts = toasts.slice(-maxToasts);

  return (
    <div className={`fixed ${getPositionClasses()} z-50 space-y-2`}>
      <AnimatePresence>
        {displayToasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onClose={hideToast}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default ToastContainer;
